// Table builders for the Model Report's "Download as XLSX". Every function here is pure: it takes
// the report data the Model Report has already shaped (pretty names applied, KPI selected) and
// returns rows as arrays of cells, so the workbook can be assembled without any chart mounted.
// SheetJS itself is passed in by the caller, which loads it on demand.

// Excel's limit for a sheet name, and the characters it rejects in one.
const SHEET_NAME_MAX_LENGTH = 31
const SHEET_NAME_INVALID_CHARS = /[[\]:*?/\\]/g

function isSeries (series) {
  return !!(series && Array.isArray(series.x) && Array.isArray(series.y))
}

function cell (value) {
  return value === undefined ? null : value
}

// Dates (2023-01-01, 2023-Q1, ...) and numbers (decay lags) sort on their own; any other label is
// left in the order the report gave it, which is the order the charts show.
function sortableKeys (keys) {
  if (keys.every(function (k) { return typeof k === 'number' })) {
    return keys.slice().sort(function (a, b) { return a - b })
  }
  if (keys.every(function (k) { return typeof k === 'string' && /^\d{4}-\d{2}/.test(k) })) {
    return keys.slice().sort()
  }
  return keys
}

// { name: { x: [...], y: [...] } } -> a wide table: one row per x (the union of every series'
// x, joined by value, since series do not always share the same dates), one column per series.
// `columns` is an optional [{ key, label }] list that fixes which series go in and in what order;
// by default every series goes in under its own name.
export function seriesMapToRows (seriesMap, firstHeader, columns) {
  if (!seriesMap || typeof seriesMap !== 'object') return null
  const cols = (columns || Object.keys(seriesMap).map(function (key) { return { key: key, label: key } }))
    .filter(function (col) { return isSeries(seriesMap[col.key]) })
  if (cols.length === 0) return null
  const xs = []
  const seen = {}
  const lookups = cols.map(function (col) {
    const series = seriesMap[col.key]
    const byX = {}
    series.x.forEach(function (x, i) {
      byX[x] = series.y[i]
      if (!Object.prototype.hasOwnProperty.call(seen, x)) {
        seen[x] = true
        xs.push(x)
      }
    })
    return byX
  })
  const rows = [[firstHeader].concat(cols.map(function (col) { return col.label }))]
  sortableKeys(xs).forEach(function (x) {
    rows.push([x].concat(lookups.map(function (byX) { return cell(byX[x]) })))
  })
  return rows
}

// A { x, y } series as a two-column table.
export function seriesToRows (series, xHeader, yHeader) {
  if (!isSeries(series)) return null
  const rows = [[xHeader, yHeader]]
  series.x.forEach(function (x, i) {
    rows.push([x, cell(series.y[i])])
  })
  return rows
}

// The model fit, straight from the report's raw series rather than from what the chart draws:
// the chart stacks the confidence band, so its "Confidence" is the band's width, and it turns
// Outliers and Epochs into markers. Here Confidence is the upper bound, and Outliers and Epochs
// keep their 0/1 flag per date.
export function modelFitToRows (raw, dateHeader) {
  if (!raw || typeof raw !== 'object') return null
  return seriesMapToRows(raw, dateHeader, [
    { key: 'Prediction', label: 'Prediction' },
    { key: 'Real', label: 'Real' },
    { key: 'Confidence_lb', label: 'Confidence_lb' },
    { key: 'Confidence_ub', label: 'Confidence' },
    { key: 'Outliers', label: 'Outliers' },
    { key: 'Epochs', label: 'Epochs' }
  ])
}

// [{ name, value }] (or a { name: value } map) as a two-column table, with an optional total row.
export function componentsToRows (components, nameHeader, valueHeader, totalLabel, total) {
  let entries = []
  if (Array.isArray(components)) {
    entries = components.map(function (c) { return [c.name, c.value] })
  } else if (components && typeof components === 'object') {
    entries = Object.keys(components).map(function (name) { return [name, components[name]] })
  }
  entries = entries.filter(function (e) { return typeof e[1] === 'number' })
  if (entries.length === 0) return null
  const rows = [[nameHeader, valueHeader]].concat(entries)
  if (totalLabel && typeof total === 'number') {
    rows.push([totalLabel, total])
  }
  return rows
}

// { freq: { x, y } } -> a long table with the frequency in the first column, one block per
// frequency in the given order.
export function byFreqToRows (byFreq, freqs, freqLabel, headers) {
  if (!byFreq) return null
  const rows = [headers]
  freqs.forEach(function (freq) {
    const series = byFreq[freq]
    if (!isSeries(series)) return
    series.x.forEach(function (x, i) {
      rows.push([freqLabel(freq), x, cell(series.y[i])])
    })
  })
  return rows.length > 1 ? rows : null
}

// { channel: { freq: { x, y } } } -> one block of rows per frequency, Date x channel inside it.
export function mediaByFreqToRows (mediaByFreq, freqs, freqLabel, granularityHeader, dateHeader) {
  if (!mediaByFreq) return null
  const channels = Object.keys(mediaByFreq)
  let header = null
  const body = []
  freqs.forEach(function (freq) {
    const seriesMap = {}
    channels.forEach(function (channel) {
      if (mediaByFreq[channel] && isSeries(mediaByFreq[channel][freq])) {
        seriesMap[channel] = mediaByFreq[channel][freq]
      }
    })
    // Every block uses the same channel columns, so the sheet reads as a single table.
    const block = seriesMapToRows(seriesMap, dateHeader, channels.map(function (c) { return { key: c, label: c } }))
    if (!block) return
    if (!header) {
      header = [granularityHeader, dateHeader].concat(channels)
    }
    const blockCols = block[0].slice(1)
    block.slice(1).forEach(function (row) {
      const byChannel = {}
      blockCols.forEach(function (c, i) { byChannel[c] = row[i + 1] })
      body.push([freqLabel(freq), row[0]].concat(channels.map(function (c) { return cell(byChannel[c]) })))
    })
  })
  return header ? [header].concat(body) : null
}

// Saturation or decay data is either { channel: {...} } or grouped as { group: { channel: {...} } }
// (a first-level 'Instant Marketing' key marks the grouped form). Returns [{ group, channels }],
// with group null for the flat form.
export function curveGroups (data) {
  if (!data || typeof data !== 'object') return []
  if (Object.prototype.hasOwnProperty.call(data, 'Instant Marketing')) {
    return Object.keys(data)
      .filter(function (group) { return data[group] && typeof data[group] === 'object' })
      .map(function (group) { return { group: group, channels: data[group] } })
  }
  return [{ group: null, channels: data }]
}

// Saturation curves as a long table: Group, Channel, Spend, Response, Marginal ROAS. The marginal
// ROAS is the curve's slope reaching each point, as the Saturation Curves chart computes it
// (empty on a channel's first point).
export function saturationCurvesToRows (data, groupLabel, headers) {
  const groups = curveGroups(data)
  const grouped = groups.length > 0 && groups[0].group !== null
  const rows = [grouped ? headers : headers.slice(1)]
  groups.forEach(function (g) {
    Object.keys(g.channels).forEach(function (channel) {
      const curve = g.channels[channel]
      if (!isSeries(curve)) return
      curve.x.forEach(function (x, i) {
        let marginal = null
        if (i > 0) {
          const dx = x - curve.x[i - 1]
          if (dx !== 0) marginal = (curve.y[i] - curve.y[i - 1]) / dx
        }
        const row = [channel, x, cell(curve.y[i]), marginal]
        rows.push(grouped ? [groupLabel(g.group)].concat(row) : row)
      })
    })
  })
  return rows.length > 1 ? rows : null
}

// The historical points plotted over each saturation curve: Group, Channel, Date, Spend, Response.
export function saturationDatapointsToRows (data, groupLabel, headers) {
  const groups = curveGroups(data)
  const grouped = groups.length > 0 && groups[0].group !== null
  const rows = [grouped ? headers : headers.slice(1)]
  groups.forEach(function (g) {
    Object.keys(g.channels).forEach(function (channel) {
      const curve = g.channels[channel]
      if (!curve || !Array.isArray(curve.datapoints)) return
      const dates = Array.isArray(curve.datapoints_dates) ? curve.datapoints_dates : []
      curve.datapoints.forEach(function (point, i) {
        if (!point) return
        const row = [channel, cell(dates[i]), cell(point.x), cell(point.y)]
        rows.push(grouped ? [groupLabel(g.group)].concat(row) : row)
      })
    })
  })
  return rows.length > 1 ? rows : null
}

// Decay curves: Lag x channel, with the group in a first column when the data is grouped.
export function decayCurvesToRows (data, groupLabel, groupHeader, lagHeader) {
  const groups = curveGroups(data)
  if (groups.length === 0) return null
  if (groups[0].group === null) {
    return seriesMapToRows(groups[0].channels, lagHeader)
  }
  const channels = []
  groups.forEach(function (g) {
    Object.keys(g.channels).forEach(function (c) {
      if (channels.indexOf(c) === -1 && isSeries(g.channels[c])) channels.push(c)
    })
  })
  const body = []
  groups.forEach(function (g) {
    const block = seriesMapToRows(g.channels, lagHeader, channels.map(function (c) { return { key: c, label: c } }))
    if (!block) return
    const blockCols = block[0].slice(1)
    block.slice(1).forEach(function (row) {
      const byChannel = {}
      blockCols.forEach(function (c, i) { byChannel[c] = row[i + 1] })
      body.push([groupLabel(g.group), row[0]].concat(channels.map(function (c) { return cell(byChannel[c]) })))
    })
  })
  return body.length ? [[groupHeader, lagHeader].concat(channels)].concat(body) : null
}

// { segment: { label: value } } -> Segment, Label, Value.
export function segmentsToRows (segments, headers) {
  if (!segments || typeof segments !== 'object') return null
  const rows = [headers]
  Object.keys(segments).forEach(function (segment) {
    const labels = segments[segment] || {}
    Object.keys(labels).forEach(function (label) {
      rows.push([segment, label, cell(labels[label])])
    })
  })
  return rows.length > 1 ? rows : null
}

// { key: value } -> a two-column table, keys sorted when they are dates or numbers.
export function objectToRows (obj, headers) {
  if (!obj || typeof obj !== 'object') return null
  const keys = sortableKeys(Object.keys(obj))
  if (keys.length === 0) return null
  return [headers].concat(keys.map(function (key) { return [key, cell(obj[key])] }))
}

// [{ name, segments: { segment: { distribution: { label: value }, variability } } }] -> Name,
// Segment, Label, Value, Variability. A segment given straight as { label: value } (no
// distribution) is read as its distribution, with no variability.
export function segmentDistributionsToRows (entries, headers) {
  const rows = [headers]
  entries.forEach(function (entry) {
    const segments = entry.segments || {}
    Object.keys(segments).sort().forEach(function (segment) {
      const s = segments[segment] || {}
      const distribution = (s.distribution && typeof s.distribution === 'object') ? s.distribution : s
      const variability = s.distribution ? cell(s.variability) : null
      Object.keys(distribution).forEach(function (label) {
        if (typeof distribution[label] !== 'number') return
        rows.push([entry.name, segment, label, distribution[label], variability])
      })
    })
  })
  return rows.length > 1 ? rows : null
}

// { names: [...], values: [...] } -> Name, Value.
export function namesValuesToRows (data, headers) {
  if (!data || !Array.isArray(data.names) || !Array.isArray(data.values)) return null
  const rows = [headers]
  data.names.forEach(function (name, i) {
    rows.push([name, cell(data.values[i])])
  })
  return rows
}

// Microaudience composition: one row per name, one column per wave and one for the projection.
// MicroaudienceComposition pushes `projected` into `waves` in place once it renders, so the
// projection is skipped wherever it shows up among the waves.
export function microaudienceCompositionToRows (microaudience, nameHeader, projectedLabel) {
  if (!microaudience || !Array.isArray(microaudience.waves)) return null
  const projected = microaudience.projected
  const columns = microaudience.waves
    .filter(function (wave) { return wave && wave !== projected && Array.isArray(wave.names) })
    .map(function (wave) { return { label: wave.wave_name, data: wave } })
  if (projected && Array.isArray(projected.names)) {
    columns.push({ label: projectedLabel, data: projected })
  }
  if (columns.length === 0) return null
  const names = []
  const lookups = columns.map(function (col) {
    const byName = {}
    col.data.names.forEach(function (name, i) {
      byName[name] = col.data.values ? col.data.values[i] : null
      if (names.indexOf(name) === -1) names.push(name)
    })
    return byName
  })
  const rows = [[nameHeader].concat(columns.map(function (col) { return col.label }))]
  names.forEach(function (name) {
    rows.push([name].concat(lookups.map(function (byName) { return cell(byName[name]) })))
  })
  return rows
}

// A valid, unique Excel sheet name. The suffix (a granularity, say) is kept whole and the title
// is cut to make room for it, so "... (Monthly)" and "... (Weekly)" never collapse into one name.
export function sheetName (title, suffix, usedNames) {
  const clean = function (s) { return String(s || '').replace(SHEET_NAME_INVALID_CHARS, ' ').replace(/\s+/g, ' ').trim() }
  const tail = suffix ? ' (' + clean(suffix) + ')' : ''
  let base = clean(title).slice(0, SHEET_NAME_MAX_LENGTH - tail.length).trim() + tail
  let name = base
  let n = 2
  while (usedNames.indexOf(name.toLowerCase()) !== -1) {
    const counter = ' ' + n
    name = base.slice(0, SHEET_NAME_MAX_LENGTH - counter.length) + counter
    n++
  }
  usedNames.push(name.toLowerCase())
  return name
}

// A file name every OS accepts.
export function safeFileName (name) {
  return String(name).replace(/[<>:"/\\|?*]/g, ' ').replace(/\s+/g, ' ').trim()
}

// The given names, with " (2)", " (3)"... before the extension of any that repeats (ignoring
// case), so files named after KPIs that sanitize alike don't overwrite each other in a ZIP.
export function uniqueFileNames (names) {
  const used = []
  return names.map(function (name) {
    const dot = name.lastIndexOf('.')
    const stem = dot > 0 ? name.slice(0, dot) : name
    const ext = dot > 0 ? name.slice(dot) : ''
    let candidate = name
    let n = 2
    while (used.indexOf(candidate.toLowerCase()) !== -1) {
      candidate = stem + ' (' + n + ')' + ext
      n++
    }
    used.push(candidate.toLowerCase())
    return candidate
  })
}

// Has the browser download a file built in memory.
function downloadBytes (bytes, fileName, mimeType) {
  const url = URL.createObjectURL(new Blob([bytes], { type: mimeType }))
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  setTimeout(function () { URL.revokeObjectURL(url) }, 0)
}

// Writes [{ title, suffix, rows }] as a workbook and has the browser download it. Sheets
// without rows are skipped.
export function downloadWorkbook (XLSX, sheets, fileName) {
  XLSX.writeFile(buildWorkbook(XLSX, sheets), fileName)
}

// Stamps `date` as the modification time of every entry of a ZIP, in place. SheetJS's ZIP writer
// leaves the time and date of each entry at zero, which unzip tools show as 1980. Both copies are
// set: the entry's record in the central directory (what listings read) and its local header.
function stampZipDates (bytes, date) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const time = (date.getHours() << 11) | (date.getMinutes() << 5) | (date.getSeconds() >> 1)
  const day = ((date.getFullYear() - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate()
  // The end-of-central-directory record closes the file (22 bytes, plus a comment SheetJS never writes).
  let end = bytes.length - 22
  while (end >= 0 && view.getUint32(end, true) !== 0x06054b50) end--
  if (end < 0) return
  const count = view.getUint16(end + 10, true)
  let offset = view.getUint32(end + 16, true)
  for (let i = 0; i < count && view.getUint32(offset, true) === 0x02014b50; i++) {
    view.setUint16(offset + 12, time, true)
    view.setUint16(offset + 14, day, true)
    const local = view.getUint32(offset + 42, true)
    if (view.getUint32(local, true) === 0x04034b50) {
      view.setUint16(local + 10, time, true)
      view.setUint16(local + 12, day, true)
    }
    offset += 46 + view.getUint16(offset + 28, true) + view.getUint16(offset + 30, true) + view.getUint16(offset + 32, true)
  }
}

// Several workbooks, [{ fileName, sheets }], in one ZIP the browser downloads. The ZIP is written
// with the CFB library bundled in SheetJS, so it needs no other dependency.
export function downloadWorkbooksZip (XLSX, files, zipName) {
  const zip = XLSX.CFB.utils.cfb_new()
  const names = uniqueFileNames(files.map(function (file) { return file.fileName }))
  files.forEach(function (file, i) {
    const bytes = XLSX.write(buildWorkbook(XLSX, file.sheets), { bookType: 'xlsx', type: 'array' })
    XLSX.CFB.utils.cfb_add(zip, names[i], new Uint8Array(bytes))
  })
  const bytes = new Uint8Array(XLSX.CFB.write(zip, { fileType: 'zip', type: 'array' }))
  stampZipDates(bytes, new Date())
  downloadBytes(bytes, zipName, 'application/zip')
}

// Each KPI's sheets, [{ kpi, sheets }]: `select` makes a KPI the page's selected one and `build`
// reads the sheets off the page's computeds, which recompute on read. The original KPI is
// selected again before returning, in the same tick, so no watcher sees the selection change and
// the page never renders the others.
export function buildPerKpi (kpis, originalKpi, select, build) {
  try {
    return kpis.map(function (kpi) {
      select(kpi)
      return { kpi: kpi, sheets: build(kpi) }
    })
  } finally {
    select(originalKpi)
  }
}

// Downloads the books of buildPerKpi: the one .xlsx when there is a single KPI, otherwise a ZIP
// with one .xlsx per KPI. `baseName(kpi)` names a file; called with null it names the ZIP.
export function downloadPerKpi (XLSX, books, baseName) {
  if (books.length === 1) {
    downloadWorkbook(XLSX, books[0].sheets, safeFileName(baseName(books[0].kpi)) + '.xlsx')
    return
  }
  downloadWorkbooksZip(XLSX, books.map(function (book) {
    return { fileName: safeFileName(baseName(book.kpi)) + '.xlsx', sheets: book.sheets }
  }), safeFileName(baseName(null)) + '.zip')
}

// [{ title, suffix, rows }] as a SheetJS workbook, one sheet each. Sheets without rows are skipped.
export function buildWorkbook (XLSX, sheets) {
  const workbook = XLSX.utils.book_new()
  const usedNames = []
  sheets.forEach(function (sheet) {
    if (!sheet.rows || sheet.rows.length < 2) return
    const worksheet = XLSX.utils.aoa_to_sheet(sheet.rows)
    // Wide enough for the header, capped so a long label does not push the data off screen.
    worksheet['!cols'] = sheet.rows[0].map(function (header) {
      return { wch: Math.min(Math.max(String(header === null ? '' : header).length + 2, 12), 40) }
    })
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName(sheet.title, sheet.suffix, usedNames))
  })
  return workbook
}
